import { env } from "@/lib/env"

// URL that points to image on S3
// export const CF_S3_URL = "http://development.images.mindtrip.ai.s3.amazonaws.com/mdubey"
// export const CF_S3_URL = "http://images.mindtrip.ai.s3.amazonaws.com"
// URL that points to images fronted by Cloudflare CDN:
// export const CF_S3_URL = "http://development-images.mindtrip.ai/mdubey"

// Cloudflare URL that points to S3
export const CF_S3_URL = "https://images.mindtrip.ai"
export const CF_IMAGE_URL = env("NEXT_PUBLIC_CF_IMAGE_URL") ?? CF_S3_URL
// Base URL for the image resize/transform endpoint. In prod this goes through Cloudflare
// (`/cdn-cgi/image`), which rewrites to image-worker. Override with NEXT_PUBLIC_CF_CGI_URL
// to point at a different host — e.g. a staging hostname or a local image-worker in dev
// (e.g. `http://localhost:3002/image-resize`). The full request URL becomes:
//   {CF_CGI_URL}/{options}/{image-path-or-opaque-token}
export const CF_CGI_URL =
  env("NEXT_PUBLIC_CF_CGI_URL") ??
  "https://d3fphkxyf5o5bm.cloudfront.net/image-resize"
// Trip Advisor URL
// const TA_URL = "https://media-cdn.tripadvisor.com/media"

// An "opaque image URL" is the base62-encoded token the backend emits in api_json
// (see SecUtils.encode_opaque_url in backend). Cloudflare /cdn-cgi/image forwards to
// image-worker which decodes the token and fetches the real image (from S3 or a remote
// CDN). Opaque tokens are a single base62 word with no slashes or scheme.
export const isOpaqueImageUrl = (src: string): boolean =>
  /^[0-9A-Za-z]{20,}$/.test(src)

export const normalizeSrc = (src: string) => {
  // Opaque tokens pass through as-is — Cloudflare routes /cdn-cgi/image/OPTIONS/TOKEN
  // to image-worker, which decodes the token and resolves the actual image source.
  if (isOpaqueImageUrl(src)) {
    return src
  }

  // Switch from our CloudFlare S3 to Cloudflare URL
  if (src.startsWith("/img")) {
    // This is our image assets in next.js (i.e. hero images in home)
    // Strip the leading "/" - these are the local images
    src = src.slice(1)
  } else if (src.startsWith("/")) {
    // This is the new way we will store images - May 5, 2023 onward
    // Prepend the cloudflare path to S3
    // use rails url for /(hotels|restaurants|attractions|etc.)/
    if (
      src.match(
        /^\/(hotels|restaurants|attractions|locations|events|airports|airlines)\//
      )
    ) {
      // override to use a different root url to serve place images from (not used for other static assets)
      src = `${CF_IMAGE_URL}${src}`
    } else {
      src = `${CF_S3_URL}${src}`
    }
  } else if (src.match(/http|https/)) {
    // For all external URL (i.e. trip advisor url).  Just send to cloudflare
  } else {
    console.log("Unexpected imgage url: " + src)
  }
  return src
}

export function isSanityImage(src: string) {
  return src.match(/^http[s]:\/\/cdn.sanity.io/)
}

export default function cloudflareLoader({
  src,
  width,
  height,
  quality,
  fit = "cover",
  blur,
}: {
  src: string
  width?: number
  height?: number
  quality?: number
  fit?: "cover" | "contain" | "scale-down"
  blur?: number
}) {
  if (!src) {
    return undefined
  }

  const isSanity = isSanityImage(src)

  const params = [`${isSanity ? "fm" : "format"}=webp`]

  if (width) {
    params.push(`w=${width}`)
  }
  if (height) {
    params.push(`h=${height}`)
  }

  if (width && height) {
    if (fit === "cover") {
      params.push(`fit=${isSanity ? "crop&crop=center" : "cover"}`)
    } else if (fit === "contain") {
      params.push(`fit=${isSanity ? "max" : "contain"}`)
    } else if (fit === "scale-down") {
      params.push(`fit=${isSanity ? "max" : "scale-down"}`)
    }
  }

  if (quality) params.push(`q=${quality}`)
  if (blur) params.push(`blur=${blur}`)

  const paramsString = params.join(isSanity ? "&" : ",")

  if (isSanity) {
    return `${src}?${paramsString}`
  } else {
    return `${CF_CGI_URL}/${paramsString}/${normalizeSrc(src)}`
  }
}
